<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-black/60 backdrop-blur-sm dark:bg-opacity-70 flex items-center justify-center p-4 z-[10001]"
    @click="$emit('close')"
  >
    <div 
      :class="[
        'bg-white dark:bg-gray-900 rounded-lg shadow-xl overflow-y-auto flex flex-col',
        sizeClasses[size]
      ]"
      @click.stop
    >
      <!-- Header -->
      <div
        v-if="title || showCloseButton"
        class="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:bg-gray-800/50 dark:border-gray-700"
      >
        <h2
          v-if="title"
          class="text-lg font-semibold text-gray-900 dark:text-white"
        >
          {{ title }}
        </h2>
        <div class="flex-1" />
        <button
          v-if="showCloseButton"
          type="button"
          class="rounded-md p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-300 transition-colors"
          @click="$emit('close')"
        >
          <svg
            class="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div
        class="flex-1 px-6 py-4"
        :class="{ 'outline-inputs': outlineInputs }"
      >
        <slot />
      </div>

      <!-- Footer with Action Buttons -->
      <div
        v-if="showActions"
        class="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700"
      >
        <BaseButton
          type="button"
          :disabled="disabled"
          :loading="loading"
          :loading-text="loadingText || 'Guardando...'"
          :class="saveClass || 'bg-blue-600 hover:bg-blue-700'"
          @click="$emit('save')"
        >
          {{ saveText || (isEditing ? 'Actualizar' : 'Crear') }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import BaseButton from './BaseButton.vue';

interface Props {
  show?: boolean
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full'
  title?: string
  showCloseButton?: boolean
  showActions?: boolean
  isEditing?: boolean
  disabled?: boolean
  loading?: boolean
  saveText?: string
  loadingText?: string
  saveClass?: string
  outlineInputs?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  size: 'md',
  title: '',
  showCloseButton: true,
  showActions: false,
  isEditing: false,
  disabled: false,
  loading: false,
  saveText: '',
  loadingText: '',
  saveClass: '',
  outlineInputs: true
})

const sizeClasses = {
  xs: 'max-w-xs w-full max-h-[90vh]',
  sm: 'max-w-md w-full max-h-[90vh]',
  md: 'max-w-2xl w-full max-h-[90vh]',
  lg: 'max-w-4xl w-full max-h-[90vh]',
  xl: 'max-w-6xl w-full max-h-[90vh]',
  full: 'w-full h-full'
}

defineEmits<{
  close: []
  save: []
}>()
</script>

<style>
.outline-inputs input,
.outline-inputs textarea,
.outline-inputs select {
  @apply dark:bg-gray-900 dark:text-white rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm;
}

.outline-inputs input[type="number"] {
  @apply pl-7 pr-12;
}

.outline-inputs textarea {
  @apply resize-none;
}

.outline-inputs select {
  @apply py-2 pl-3 pr-10 text-base;
}
</style>
